// Show-start / scheduled-time handling.
//
// The show start is an ABSOLUTE INSTANT shown in each viewer's OWN timezone:
// someone in France setting 08:00 (CEST) stores 06:00Z, and a UK operator sees
// it as 07:00 (BST). So we store via toISOString() and read via local getters.
//
// The subtle bug this centralises the fix for: the value reaches readers from
// two sources in different string formats. The REST response (and the machine
// that just set it) carries a proper ISO instant with a "Z" — new Date() reads
// that as UTC, correctly. But other machines load the show from the local
// PowerSync store, where a Prisma `timestamp` column serialises WITHOUT a zone,
// e.g. "2026-08-05 06:00:00". new Date() parses a zone-less string as LOCAL, so
// after a refresh every machine showed the raw UTC clock instead of a proper
// local conversion — the "strange times" report. toInstant() normalises those
// zone-less timestamps as UTC (which is how Prisma stores them) so every reader
// agrees regardless of source.

function pad2(n) { return String(n).padStart(2, '0'); }

// Parse a stored scheduled time to an absolute-instant Date, robust to both
// ISO-with-zone strings (REST) and zone-less Postgres timestamps (PowerSync,
// which are UTC). Returns Date | null.
export function toInstant(value) {
  if (!value) return null;
  if (value instanceof Date) return isNaN(value.getTime()) ? null : value;
  let s = String(value).trim();
  // Zone-less "YYYY-MM-DD[ T]HH:MM[:SS[.sss]]" — treat as UTC (Prisma stores UTC).
  if (/^\d{4}-\d{2}-\d{2}[ T]\d{2}:\d{2}(:\d{2})?(\.\d+)?$/.test(s)) {
    s = s.replace(' ', 'T') + 'Z';
  }
  const d = new Date(s);
  return isNaN(d.getTime()) ? null : d;
}

// "HH:MM"/"HH:MM:SS" typed by the operator (in THEIR local zone) → ISO instant.
// Returns null if the string isn't a valid time.
export function hmsToScheduledStart(hms) {
  const trimmed = String(hms || '').trim();
  if (!/^\d{1,2}:\d{2}(:\d{2})?$/.test(trimmed)) return null;
  const [h = 0, m = 0, s = 0] = trimmed.split(':').map(Number);
  if (h > 23 || m > 59 || s > 59) return null;
  const d = new Date();
  d.setHours(h, m, s, 0);
  return d.toISOString();
}

// Stored instant → "HH:MM:SS" in the VIEWER's local zone. '' if unset/invalid.
export function extractShowStartHMS(value) {
  const d = toInstant(value);
  if (!d) return '';
  return `${pad2(d.getHours())}:${pad2(d.getMinutes())}:${pad2(d.getSeconds())}`;
}

// Resolve a show's start clock to a concrete Date on TODAY (local), carrying the
// stored instant's local time-of-day. Anchors cue in-time maths. Date | null.
export function parseShowStartDate(show) {
  const d = toInstant(show?.show_start_time || show?.scheduled_start);
  if (!d) return null;
  const today = new Date();
  today.setHours(d.getHours(), d.getMinutes(), d.getSeconds(), 0);
  return today;
}

// ── Dashboard "scheduled date/time" ─────────────────────────────────────────
// Same absolute-instant model, but with a full date + time.

// datetime-local "YYYY-MM-DDTHH:mm" (viewer-local) → ISO instant.
export function datetimeLocalToISO(local) {
  const s = String(local || '').trim();
  if (!s) return null;
  const d = new Date(s); // a zoned-less datetime-local string is parsed as LOCAL
  return isNaN(d.getTime()) ? null : d.toISOString();
}

// Stored instant → "YYYY-MM-DDTHH:mm" (viewer-local) for a datetime-local input.
export function isoToDatetimeLocal(iso) {
  const d = toInstant(iso);
  if (!d) return '';
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}T${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
}

// Stored instant → human label in the viewer's local zone.
export function formatShowDateTime(iso, opts = { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) {
  const d = toInstant(iso);
  if (!d) return '—';
  return d.toLocaleString(undefined, opts);
}
